/**
 * HowUnique Component - Simple Expert Refactor
 * 
 * Improvements:
 * - Better state management with useCallback
 * - Cleaner card data structure
 * - Memoized components for performance
 * - Proper prop validation and error handling
 * - Enhanced accessibility
 */

import React, { useState, useCallback, useMemo, memo } from "react";
import { NavLink } from "react-router-dom";

// Default icons
import icon1 from "@assets/images/how_they_are_unique_icons/icon1.png";
import icon2 from "@assets/images/how_they_are_unique_icons/icon2.png";
import icon3 from "@assets/images/how_they_are_unique_icons/icon3.png";
import icon4 from "@assets/images/how_they_are_unique_icons/icon4.png";

// Components
import Modal from "@modals/Modal";
import Popup from "@modals/Popup";
import HowUniquePopup from "@modals/HowUniquePopup";

// Context
import { useAppContext } from "@context/AppContext";

// Default icons array for fallback
const DEFAULT_ICONS = [icon1, icon2, icon3, icon4];

// Memoized Card Component
const UniqueCard = memo(({ item, onCardClick }) => {
  const handleClick = useCallback(() => {
    onCardClick(item.id);
  }, [item.id, onCardClick]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onCardClick(item.id);
    }
  }, [item.id, onCardClick]);

  const iconSrc = useMemo(() => {
    return item.type === "link" ? `/image/${item.icon}` : item.icon;
  }, [item.type, item.icon]);

  return (
    <div
      className="how-unique__card"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Learn more about ${item.title}`}
    >
      <div className="how-unique__card-icon-box">
        <img
          className={`how-unique__card-icon icon${item.id + 1}`}
          src={iconSrc}
          alt={`${item.title} icon`}
          loading="lazy"
        />
      </div>
      <h3 className="how-unique__card-title">{item.title}</h3>
      <div className="how-unique__card-btn">
        <span className="how-unique__card-btn-text">Learn More</span>
      </div>
    </div>
  );
});
UniqueCard.displayName = 'UniqueCard';

// Main Component
const HowUnique = memo(() => {
  const { themeConfig } = useAppContext();
  const [modalState, setModalState] = useState({
    isOpen: false,
    selectedCardId: 0
  });

  // Memoized card data
  const howUniqueCards = useMemo(() => {
    const cardConfig = [
      {
        iconKey: 'APP_ABOUNT_PAGE_ICON1',
        titleKey: 'APP_ABOUT_CARD_TITLE1',
        descKey: 'APP_ABOUT_CARD_DESC1'
      },
      {
        iconKey: 'APP_ABOUNT_PAGE_ICON2',
        titleKey: 'APP_ABOUT_CARD_TITLE2',
        descKey: 'APP_ABOUT_CARD_DESC2'
      },
      {
        iconKey: 'APP_ABOUNT_PAGE_ICON3',
        titleKey: 'APP_ABOUT_CARD_TITLE3',
        descKey: 'APP_ABOUT_CARD_DESC3'
      },
      {
        iconKey: 'APP_ABOUNT_PAGE_ICON4',
        titleKey: 'APP_ABOUT_CARD_TITLE4',
        descKey: 'APP_ABOUT_CARD_DESC4'
      }
    ];

    return cardConfig.map((config, index) => {
      const customIcon = themeConfig?.[config.iconKey];
      return {
        id: index,
        icon: customIcon || DEFAULT_ICONS[index],
        title: themeConfig?.[config.titleKey] || `Feature ${index + 1}`,
        desc: themeConfig?.[config.descKey] || '',
        type: customIcon ? "link" : "raw"
      };
    });
  }, [themeConfig]);

  // Event handlers
  const openModal = useCallback((cardId) => {
    setModalState({
      isOpen: true,
      selectedCardId: cardId
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      selectedCardId: 0
    });
  }, []);

  // Get selected card for modal
  const selectedCard = useMemo(() => 
    howUniqueCards[modalState.selectedCardId] || howUniqueCards[0]
  , [howUniqueCards, modalState.selectedCardId]);

  return (
    <>
      <span className="link_wrapper" id="about" aria-label="About section"></span>
      <section 
        className="how-unique default-settings gradient-bg"
        aria-labelledby="how-unique-title"
      >
        <h1 id="how-unique-title" className="how-unique__title title-ds">
          {themeConfig?.APP_ABOUT_CARD_TITLE || 'How We Are Unique'}
        </h1>
        <h1 className="how-unique__title title-mb">
          {themeConfig?.APP_ABOUT_CARD_TITLE || 'How We Are Unique'}
        </h1>
        
        <div className="hr-line" role="separator"></div>
        
        <p className="how-unique__desc">
          {themeConfig?.APP_ABOUT_CARD_DESC || 'Discover what makes us different'}
        </p>

        <div className="how-unique__cards" role="grid">
          {howUniqueCards.map((item) => (
            <UniqueCard
              key={item.id}
              item={item}
              onCardClick={openModal}
            />
          ))}
        </div>

        <NavLink 
          to="/faqs" 
          className="faqs-link-wrapper"
          aria-label="View frequently asked questions"
        >
          <h3 className="faqs-link">More Answers to Common Questions</h3>
        </NavLink>

        {/* Modal */}
        {modalState.isOpen && (
          <Modal show={modalState.isOpen}>
            <Popup>
              <HowUniquePopup
                item={selectedCard}
                onClose={closeModal}
              />
            </Popup>
          </Modal>
        )}
      </section>
    </>
  );
});

HowUnique.displayName = 'HowUnique';

export default HowUnique;
